import { useEffect } from "react"
import { X } from "lucide-react"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { useClientTheme } from "@/app/components/ThemeContext"
import type { Well } from "@/app/types/inventory"

interface Props {
  well: Well
  onClose: () => void
}

// Normalize the `interests` field (comma/slash string, or an array that
// stringifies to a comma-joined list) into chips.
function interestList(interests: Well["interests"]): string[] {
  if (!interests) return []
  return String(interests)
    .split(/[,/]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export function WellSummaryModal({ well, onClose }: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  const interests = interestList(well.interests)
  // Decimal interests: only show the figures that are actually recorded.
  const decimals = [
    { label: "Working Interest (WI)", value: well.wi },
    { label: "Royalty Interest (RI)", value: well.ri },
  ].filter((d) => d.value != null && String(d.value).trim() !== "")

  return (
    <div
      className={`fixed inset-0 z-[60] flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    >
      <div
        className={`relative overflow-hidden flex flex-col ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          border: "none",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(560px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? "100dvh" : "88vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}`, flexShrink: 0 }}
        >
          <div>
            <h2
              style={{
                fontFamily: "var(--font-data)",
                fontSize: "17px",
                fontWeight: 700,
                color: theme.textPrimary,
              }}
            >
              {well.well_name}
            </h2>
            <p
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.textMuted,
                marginTop: "2px",
              }}
            >
              {well.operator_display || well.operator || "Operator not recorded"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ml-4"
            style={{
              color: theme.textMuted,
              backgroundColor: theme.bgSurface2,
              border: `1px solid ${theme.borderColor}`,
            }}
          >
            <X size={14} />
          </button>
        </div>

        <div className="overflow-auto flex-1 px-6 py-5 flex flex-col gap-5">
          {/* Interests */}
          <div className="flex flex-col gap-2">
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                color: theme.textDim,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Interests
            </span>
            {interests.length === 0 ? (
              <span
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textDim }}
              >
                —
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {interests.map((it) => (
                  <span
                    key={it}
                    className="inline-block px-2.5 py-0.5 rounded-full"
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "11px",
                      fontWeight: 500,
                      backgroundColor: `${theme.brandTeal}1A`,
                      color: theme.brandTeal,
                    }}
                  >
                    {it}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Decimal Interests — clean two-stat layout */}
          <div className="flex flex-col gap-2">
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                color: theme.textDim,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Decimal Interests
            </span>
            {decimals.length === 0 ? (
              <span
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textDim }}
              >
                None recorded
              </span>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {decimals.map((d) => (
                  <div
                    key={d.label}
                    className="rounded-lg px-3 py-2.5"
                    style={{
                      backgroundColor: theme.bgSurface2,
                      border: `1px solid ${theme.borderSubtle}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "10px",
                        color: theme.textMuted,
                        marginBottom: "2px",
                      }}
                    >
                      {d.label}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-data)",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: theme.textPrimary,
                      }}
                    >
                      {d.value}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Associated STRs */}
          <div className="flex flex-col gap-2">
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                color: theme.textDim,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Sections (STR)
            </span>
            {well.strs.length === 0 ? (
              <span
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textDim }}
              >
                —
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {well.strs.map((s) => (
                  <span
                    key={s}
                    className="inline-block px-2.5 py-1 rounded-md"
                    style={{
                      fontFamily: "var(--font-data)",
                      fontSize: "12px",
                      color: theme.textPrimary,
                      backgroundColor: theme.bgSurface2,
                      border: `1px solid ${theme.borderSubtle}`,
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
